import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { CircleAlert, Loader2 } from "lucide-react";
import { useSeedListCancelPlanContext } from "./seed-list-cancel-provider";
import { format } from "date-fns";
import { PaymentInterval } from "@/types/subscription.types";
import { useMemo } from "react";
import { useCancelSenderProfileSubscription } from "@/hooks/use-subscription";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";

const SeedListCancelConfirmation = () => {
  const { state, planDetails, subscription, searchParams, handleStateChange } =
    useSeedListCancelPlanContext();
  
  const { mutateAsync: cancelSenderProfileSubscription, isPending: isCancelling } =
    useCancelSenderProfileSubscription();
  const navigate = useNavigate();

  if (!planDetails || !subscription) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            {!planDetails && !subscription
              ? "Loading plan details..."
              : !planDetails
              ? "Plan details not found"
              : "Subscription not found"}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate seed list plan price using the new API structure
  const calculateSeedListPrice = useMemo(() => {
    const SEED_PRICE_PER_UNIT = 0.30;
    const SENDER_PROFILE_MULTIPLIER = 0.60;

    // Get seed accounts and sender profiles from plan details
    // seedCountPerSender is the number of seeds per sender profile
    // quantity is the number of sender profiles
    const seedCountPerSender = planDetails.seedCountPerSender || 500;
    const senderProfiles = planDetails.quantity || planDetails.maxSenderProfilesMonthly || 1;
    
    // Total seed accounts = seedCountPerSender * quantity (number of sender profiles)
    const seedAccounts = seedCountPerSender * senderProfiles;

    const basePrice = seedAccounts * SEED_PRICE_PER_UNIT;
    const senderProfileCost = basePrice * SENDER_PROFILE_MULTIPLIER * senderProfiles;
    const finalPrice = basePrice + senderProfileCost;

    let totalPrice: number;
    if (subscription.interval === PaymentInterval.ANNUAL) {
      totalPrice = finalPrice * 12 * 0.8;
    } else if (subscription.interval === PaymentInterval.SEMI_YEARLY) {
      totalPrice = finalPrice * 6 * 0.85;
    } else if (subscription.interval === PaymentInterval.QUARTERLY) {
      totalPrice = finalPrice * 3 * 0.9;
    } else {
      totalPrice = finalPrice;
    }

    return {
      monthlyPrice: finalPrice,
      totalPrice: totalPrice * searchParams.quantity,
      seedAccounts,
      senderProfiles,
      seedCountPerSender,
    };
  }, [planDetails, subscription, searchParams.quantity]);

  const renderDecreasedPriceBasedOnInterval = () => {
    const { totalPrice } = calculateSeedListPrice;

    if (subscription.interval === PaymentInterval.ANNUAL) {
      return (totalPrice / 12).toFixed(2);
    }

    if (subscription.interval === PaymentInterval.QUARTERLY) {
      return (totalPrice / 3).toFixed(2);
    }

    if (subscription.interval === PaymentInterval.SEMI_YEARLY) {
      return (totalPrice / 6).toFixed(2);
    }

    return totalPrice.toFixed(2);
  };

  const handleBack = () => handleStateChange({ step: state.step - 1 });
  
  const handleConfirm = async () => {
    if (!planDetails) {
      toast.error("Plan details not found");
      return;
    }

    try {
      // Call the cancel sender profile subscription API
      await cancelSenderProfileSubscription({
        planId: planDetails.planId,
      });

      toast.success("Sender profile subscription cancelled successfully");

      // Track analytics
      window.cioanalytics?.track("sender_profile_subscription_cancelled", {
        planId: planDetails.planId,
        subscriptionId: subscription?._id,
        quantity: planDetails.quantity,
        espTypes: planDetails.espTypes,
      });

      // Navigate to success or dashboard
      navigate({ to: "/dashboard" });
    } catch (error) {
      console.error("Error cancelling sender profile subscription:", error);
      toast.error("Failed to cancel subscription. Please try again.");
    }
  };

  // Generate plan name from available data
  const planName = useMemo(() => {
    if (planDetails.espTypes && planDetails.espTypes.length > 0) {
      const espType = planDetails.espTypes[0];
      const seedCount = planDetails.seedCountPerSender || 500;
      return `Sender Profile - ${espType} - ${seedCount} seeds`;
    }
    return planDetails.isCustomPlan ? "Custom Seed List Plan" : "Seed List Plan";
  }, [planDetails]);

  // Get subscription end date
  const subscriptionEndDate = planDetails.subscriptionEndDate || subscription.endDate;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Confirm Cancellation</CardTitle>
        <CardDescription>
          Please review the information below before confirming
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-900/20 rounded-lg p-4">
            <h4 className="font-semibold text-red-800 dark:text-red-400 mb-2">
              Cancellation Summary
            </h4>
            <ul className="space-y-2 text-red-700 dark:text-red-400">
              <li className="flex items-center gap-2">
                <CircleAlert className="h-4 w-4 shrink-0" />
                <span>
                  Your{" "}
                  <strong>{planName}</strong>{" "}
                  will be canceled and remain active until{" "}
                  <strong>{format(new Date(subscriptionEndDate), "MMM d, yyyy")}</strong>
                  .
                </span>
              </li>
              <li className="flex items-center gap-2">
                <CircleAlert className="h-4 w-4 shrink-0" />
                <span>
                  After this date, you will lose access to all seed list features
                  including {calculateSeedListPrice.seedCountPerSender.toLocaleString()} seeds per sender profile,{" "}
                  {calculateSeedListPrice.senderProfiles} sender profile{calculateSeedListPrice.senderProfiles > 1 ? "s" : ""} (total of {calculateSeedListPrice.seedAccounts.toLocaleString()} seed accounts).
                  {planDetails.espTypes && planDetails.espTypes.length > 0 && (
                    <> ESP Types: {planDetails.espTypes.join(", ")}.</>
                  )}
                </span>
              </li>
              <li className="flex items-center gap-2">
                <CircleAlert className="h-4 w-4 shrink-0" />
                <span>
                  No further charges ($
                  {renderDecreasedPriceBasedOnInterval()}/
                  {subscription.interval.toLowerCase()}) will be applied after
                  cancellation.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <CircleAlert className="h-4 w-4 shrink-0" />
                <span>
                  Once plan is cancelled you will not be able to access your
                  seed list plan.
                </span>
              </li>
            </ul>
          </div>

          <Separator />
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={handleBack}>
          Back
        </Button>
        <Button 
          variant="destructive" 
          onClick={handleConfirm}
          disabled={isCancelling}
        >
          {isCancelling ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Cancelling...
            </>
          ) : (
            "Confirm Cancellation"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default SeedListCancelConfirmation;

